import   React                  ,
       { useState               ,
	     useEffect              ,
		 useRef                 }  from 'react';

import   classNames                from 'classnames';

import   styles                    from './ProductSelector.module.scss';

import { Product                ,
         ProductCategory        }  from '../../../dataTypes/productManagment/types';


import { searchImage            }  from '../../../dataTypes/productManagment/utils';

import   Select                    from '../../widget/inputs/Select';
import   Field                     from '../../widget/inputs/Field';
import   Trigger                   from '../../widget/actuators/Trigger';
import   Box                       from '../../widget/windows/Box';
import   ProductSheet              from './ProductSheet';


import { useTypedSelector       ,
         useTypedDispatch       }  from '../../../store/useTypeSelector';
import { fetchProductList    }  from '../../../store/productManagment/actions';
import { fetchProductCategories }  from '../../../store/productCategoryManagment/actions';

export interface ProductSelectorProps {
  productSet         : Product[];
  productCategories  : ProductCategory[];
  onCancel?          : () => void | null;
  onSelect?          : (id: number) => void | null;
  thumbnailWidth?    : number;
  thumbnailHeight?   : number;
  componentWidth?    : number;
  componentHeight?   : number;
  originalFilter?    : string[];
  productTypeNames?  : string[];
  productTypeLabels? : string[];
}

// ✅ EN DEHORS du composant (avant export const ProductSelector)
const DEFAULT_ORIGINAL_FILTER: string[] = [];
const DEFAULT_PRODUCT_TYPE_NAMES: string[] = [];

export const ProductSelector = ({ productSet                                                              ,
                                  productCategories                                                       ,
                                  productTypeNames   : customProductTypeNames  = DEFAULT_PRODUCT_TYPE_NAMES ,
                                  productTypeLabels  : customProductTypeLabels                            ,
                                  originalFilter                               = DEFAULT_ORIGINAL_FILTER  ,
                                  onCancel                                                                ,
                                  onSelect                                                                ,
                                  thumbnailWidth                               = 300                      ,
                                  thumbnailHeight                              = 170                      ,
                                  componentWidth                               = 1210                     ,
                                  componentHeight                              = 626                      }: ProductSelectorProps) => {

  const dispatch = useTypedDispatch();

  const products   = useTypedSelector(state => state.productListReducer.products);
  // Valeur inutilisée, mais l'abonnement est conservé : le retirer changerait
  // la fréquence de rendu du composant.
  useTypedSelector(state => state.productListReducer.loading);
  const categories = useTypedSelector(state => state.productCategoryReducer.categories);
  const { authentication } = useTypedSelector(state => state.authenticationReducer);

  const productArray  = Array.from(products.values());
  const categoryArray = Array.from(categories.values());

  const [ productCategorySet       ] = useState<ProductCategory[]>(productCategories ?? categoryArray);
  const [ originalProductSet       , setOriginalProductSet       ] = useState<Product[]>(productArray);
  const [ filteredProductSet       , setFilteredProductSet       ] = useState<Product[]>(productArray);
  const [ selectedProductType      , setSelectedProductType      ] = useState(( customProductTypeNames.length === 1 && !customProductTypeNames.includes('Tous') ) ? customProductTypeNames[0] : 'Tous');
  const [  , setSelectedProductTypeIndex ] = useState(0);
  const [ selectedCategory         , setSelectedCategory         ] = useState('Toutes');
  const [ selectedCategoryIndex    , setSelectedCategoryIndex    ] = useState(0);
  const [ selectedSortingCriteria  , setSelectedSortingCriteria  ] = useState('Aucun tri');
  const [ filter                   , setFilter                   ] = useState('');
  const [ showCreateWindow         , setShowCreateWindow         ] = useState(false);

  const [ imageUrls, setImageUrls] = useState<{ [key: number]: string }>({});

  const creationWindowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {

	const typesToFetch = originalFilter.length === 0 || originalFilter.includes('Tous')
      ? [ 'Physical Item', 'Subscription', 'PayVideo', 'PayChannel', 'Urban Tour Masterclass']
      : originalFilter;

    const filtered = (productSet ?? []).filter((m) => typesToFetch.includes(m.kind ?? ' '));

    setOriginalProductSet(filtered);

  }, [originalFilter, productSet, customProductTypeNames]);

  useEffect(() => {
    filterProducts();
  }, [selectedProductType, selectedCategory, selectedSortingCriteria, filter]);

  useEffect(() => {
    if (showCreateWindow && creationWindowRef.current)
	 {
      creationWindowRef.current.scrollTop = 0;
     }
  }, [showCreateWindow]);

  useEffect(() => {

    // Réinitialiser la catégorie si elle n'est plus disponible pour ce type de média
    if (selectedProductType !== 'Tous') {

      const availableCategories = productCategorySet.filter(
        (category) => category.name === selectedProductType
      );

      if (availableCategories.length === 0 ||
           (selectedCategory !== 'Toutes' &&
            !availableCategories.some(cat => cat.name === selectedCategory))) {
        setSelectedCategory('Toutes');
        setSelectedCategoryIndex(0);
      }
    }
  }, [selectedProductType, productCategorySet]);


  // Charger les données
  useEffect(() => {
    dispatch(fetchProductList({}, '', false, authentication.admin?.id));
    dispatch(fetchProductCategories(false, authentication.admin?.id));

	console.log ("useEffect ProductSelector", products, categories)
  }, [dispatch]);


  // AJOUTEZ CE NOUVEAU useEffect
  useEffect(() => {

    const productArray = Array.from(products.values());
    setOriginalProductSet(productArray);
    setFilteredProductSet(productArray);
  }, [products]); // Se déclenche quand products change


  useEffect(() => {

    const loadImages = async () => {
      const urls: { [key: number]: string } = {};
      for (const product of productSet) {
        const url = await searchImage(product);
        urls[product.id] = url || '';
      }
      setImageUrls(urls);
    };
    loadImages();
  }, [productSet]);

  // Utiliser les données

  const handleClick = (productId: number) => {

    if (onSelect)
     {
      onSelect(productId);
     }
  };

  const renderProductThumbnail = (product: Product): JSX.Element => {
    const urlImage = imageUrls[product.id];

    return (
      <div
        className={classNames(styles.thumbnail)}
        onClick={() => handleClick(product.id)}
        style={{ width: thumbnailWidth, height: thumbnailHeight + 100 }}
      >
        <div className={classNames(styles.line_1)}>
          {urlImage ? (
            <img
              src={urlImage}
              alt={product.name}
              style={{
                width: '100%',  // Utilise toute la largeur de la boîte
                height: thumbnailHeight, // Utilise toute la hauteur de la boîte
                objectFit: 'contain',  // Conserve le ratio de l'image sans déformation
              }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: thumbnailHeight,
                backgroundColor: '#000000', // Vous pouvez définir une couleur de fond pour simuler une image manquante
              }}
            />
          )}
        </div>
        <div className={classNames(styles.line_2, styles.text)}>
          <>
		   {product.name}
		   <br/>
		   Ref: {product.code}
		   <br/>
		   {
            product.variants.length > 0 && product.variants[0].prices.length > 0
            ?
            `${product.variants[0].prices[0].priceWithTax.toFixed(2)} €`
            :
            null
           }
		  </>
        </div>
      </div>
    );
  };

  // ✅ CORRECTION : Ajout des colonnes vides comme dans MediaSelector
  const renderProductThumbnailsList = (): JSX.Element => {
    const thumbnailsPerRow = Math.floor(componentWidth / thumbnailWidth);
    const rows: JSX.Element[] = [];

    for (let i = 0; i < filteredProductSet.length; i += thumbnailsPerRow) {
      const rowProducts = filteredProductSet.slice(i, i + thumbnailsPerRow);
      const emptyColumns = thumbnailsPerRow - rowProducts.length;

      rows.push(
        <div key={`row-${i}`} className={classNames(styles.row)}>
          {rowProducts.map((product) => (
            <div key={product.id} className={classNames(styles.column)}>
              {renderProductThumbnail(product)}
            </div>
          ))}
          {/* ✅ AJOUT : Colonnes vides pour aligner la grille */}
          {Array.from({ length: emptyColumns }).map((_, index) => (
            <div key={`empty-${i}-${index}`} className={classNames(styles.column)} style={{ width: thumbnailWidth }}>
              {/* Colonne vide pour maintenir l'alignement */}
            </div>
          ))}
        </div>
      );
    }

    return <div className={classNames(styles.product_grid)}>{rows}</div>;
  };

  const filterProducts = () => {
    if (originalProductSet.length > 0) {
      let filteredProducts = originalProductSet.filter((product) => {
        const matchesCategory =
          selectedCategory === 'Toutes' || (selectedCategoryIndex > 0 && product.categoriesIds.includes(productCategories[selectedCategoryIndex - 1].id));
        const matchesFilter = product.name.toLowerCase().includes(filter.toLowerCase());
        return matchesCategory && matchesFilter;
      });

      // Tri par date ou durée
      if (selectedSortingCriteria === 'Par date de sortie croissante') {
        filteredProducts.sort((a, b) => (a.creationDate?.getTime() || 0) - (b.creationDate?.getTime() || 0));
        console.log("cas 1", filteredProducts);
      } else if (selectedSortingCriteria === 'Par date de sortie décroissante') {
        filteredProducts.sort((a, b) => (b.creationDate?.getTime() || 0) - (a.creationDate?.getTime() || 0));
        console.log("cas 2", filteredProducts);
      }

      setFilteredProductSet(filteredProducts);
    }
  };

  const handleProductTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProductType(e.target.value);
    setSelectedProductTypeIndex(e.target.selectedIndex);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
    setSelectedCategoryIndex(e.target.selectedIndex);
  };

  const handleSortingCriteriaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSortingCriteria(e.target.value);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
  };

  const renderProductTypeSelection = (): JSX.Element | null => {
    if (customProductTypeNames.length > 0) {
      let types = [...customProductTypeNames];
      let labels = customProductTypeLabels ? [...customProductTypeLabels] : types;
      if (types.length > 1) {
        types.unshift('Tous');
        labels.unshift('Tous');
      }
      if (types.length === 1) {
        return null;
      }
      return (
        <Select
          choices={types}
          choicesLabel={labels}
          value={selectedProductType}
          onChange={handleProductTypeChange}
        />
      );
    }
    const defaultProductTypeNames  = [ 'Tous' , 'Physical Item'      , 'Subscription' , 'PayVideo'      , 'PayChannel'       , 'Urban Tour Masterclass' ];
    const defaultProductTypeLabels = [ 'Tous' , 'Produits physiques' , 'Abonnements'  , 'Vidéo payante' , 'Chaînes payantes' , 'Masterclass Urban Tour' ];
    return (
      <Select
        choices={defaultProductTypeNames}
        choicesLabel={defaultProductTypeLabels}
        value={selectedProductType}
        onChange={handleProductTypeChange}
      />
    );
  };



  const renderCategoriesSelection = (): JSX.Element => {
    const categoryNames = ['Toutes', ...productCategories.map((category) => category.name)];
    return (
      <Select
        choices={categoryNames}
        value={selectedCategory}
        onChange={handleCategoryChange}
      />
    );
  };

  const renderSortingCriteria = (): JSX.Element => {
    const sortingCriteriaNames = [
      'Aucun tri',
      'Par date de sortie croissante',
      'Par date de sortie décroissante',
    ];

    return (
      <Select
        choices={sortingCriteriaNames}
        value={selectedSortingCriteria}
        onChange={handleSortingCriteriaChange}
      />
    );
  };

  const handleCloseButtonClick = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleCreationWindowShow = () => {
    setShowCreateWindow(true);
  };

  const handleCreationWindowCancel = () => {
    setShowCreateWindow(false);
  };


  const handleProductCreate = (id: number) => {
    setShowCreateWindow(false);
  };

  return (
    <>
      {showCreateWindow && (
        <Box
          onClose={handleCreationWindowCancel}
          width="90vw"
          height="90vh"
          marginTop="0px"
          contentMargin="10px"
        >
		 {
		  selectedProductType !== 'Tous'
		  ?
          <ProductSheet onCreate={handleProductCreate} productKind={selectedProductType}/>
		  :
          <ProductSheet onCreate={handleProductCreate}/>
		 }
        </Box>
      )}
      <Box
        onClose={handleCloseButtonClick}
        width={`${componentWidth + 70}px`}
        height={`${componentHeight}px`}
        marginTop="0px"
        contentMargin="10px"
        zIndex={9900}
      >
        <>
          <div className={classNames(styles.upper_bar)}>
            {renderProductTypeSelection()}
            {renderCategoriesSelection()}
            {renderSortingCriteria()}
            <Field
              type="text"
              placeholder="Filtrer par titre"
              value={filter}
              onChange={handleFilterChange}
            />
            <div className={classNames(styles.productCreationButton)}>
              <Trigger
                name="Créer un produit"
                onClick={handleCreationWindowShow}
                variant="tailwind2"
              />
            </div>
          </div>
          <div className={classNames(styles.product_grid)}>
            {renderProductThumbnailsList()}
          </div>
        </>
      </Box>
    </>
  );
};

export default ProductSelector;
